import { HttpClient, HttpParams } from '@angular/common/http';
import { CacheService } from '../cache';
import { ResourceService } from './resource';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Flight } from '../../models/flight';

@Injectable({
  providedIn: 'root'
})
export class FlightService extends ResourceService<Flight> {
  /**
   * @constructor
   * Creates an instance of the EnquiresService
   *
   * @param {HttpClient} http Injected instance of HttpClient
   * @param {CacheService} cache Injected instance of CacheService
 */
  constructor(
    protected http: HttpClient,
    protected cache: CacheService
  ) {
    super(http, cache, 'flights');
  }

 /**
  * Sends a POST request to the /flights/search endpoint
  * @remarks
  * to search for flight with required payload based on search type // e.g multi destination, one way, round trip
  * 
  * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
  */
  searchflight(payload: any) {
    return this.http.post<any>(`${this.endpoint}/search/all`, payload)
      .pipe(map((resp: any) => {
        this.cache.remove(`${this.endpoint}/search`);
        this.cache.remove(`${this.endpoint}/search`);
        return resp;
      }))
  }

   /**
  * Sends a GET request to the /flights/airport endpoint
  * @remarks
  * to get all airports or specific airport if param is supplied
  * 
  * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
  */
  getAirports(params?: any) {
    return this.http.get<any>(`${this.endpoint}/airports`, {params})
      .pipe(map((resp: any) => {
        this.cache.remove(`${this.endpoint}/airports`);
        this.cache.remove(`${this.endpoint}/airports`);
        return resp;
      }))
  }


   /**
  * Sends a GET request to the /flights/:id endpoint
  * @remarks
  * to get detail of a flight by providing the flight bundle number
  * 
  * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
  */
  getFlightDetails(id: string, params?:any) {
    return this.http.get<any>(`${this.endpoint}/${id}`, {params})
      .pipe(map((resp: any) => {
        this.cache.remove(`${this.endpoint}/${id}`);
        this.cache.remove(`${this.endpoint}/${id}`);
        return resp;
      }))
  }




   /**
  * Sends a GET request to the /booking/:id endpoint
  * @remarks
  * to get detail of a flight booking by providing the flight booking id
  * 
  * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
  */
  getFlightBookingDetails(id: string, params?: any) {
    return this.http.get<any>(`bookings/${id}`, {params})
      .pipe(map((resp: any) => {
        this.cache.remove(`bookings/${id}`);
        this.cache.remove(`bookings/${id}`);
        return resp;
      }))
  }


   /**
  * Sends a POST request to the /flights/book endpoint
  * @remarks
  * to book a flight
  * 
  * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
  */
  bookFlight(payload: any, provider?: any) {
    return this.http.post<any>(`bookings/flight?provider=${provider}`, payload)
      .pipe(map((resp: any) => {
        this.cache.remove(`bookings/flight`);
        this.cache.remove(`bookings/flight`);
        return resp;
      }))
  }

   /**
  * Sends a PUT request to the /flights/book/${id} endpoint
  * @remarks
  * to update flight booking
  * 
  * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
  */
  updateFlightBooking(id: string, payload: any) {
    return this.http.put<any>(`bookings/flight/${id}`, payload)
      .pipe(map((resp: any) => {
        this.cache.remove(`bookings/flight/${id}`);
        this.cache.remove(`bookings/flight/${id}`);
        return resp;
      }))
  }

   /**
  * Sends a PUT request to the /flights/book/${id} endpoint
  * @remarks
  * to modify flight booking
  * 
  * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
  */
  modifyFlightBooking(id: string, payload: any) {
    return this.http.put<any>(`bookings/${id}/modify?booking_service=flight`, payload)
      .pipe(map((resp: any) => {
        this.cache.remove(`bookings/${id}/modify?booking_service=flight`);
        this.cache.remove(`bookings/${id}/modify?booking_service=flight`);
        return resp;
      }))
  }

   /**
  * Sends a PUT request to the /bookings/flight/cancel endpoint
  * @remarks
  * to request cancel flight booking
  * 
  * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
  */
  requestCancelFlightBooking(payload: any) {
    return this.http.put<any>(`bookings/flight/cancel`, payload)
      .pipe(map((resp: any) => {
        this.cache.remove(`bookings/flight/cancel`);
        this.cache.remove(`bookings/flight/cancel`);
        return resp;
      }))
  }

   /**
  * Sends a PUT request to the bookings/:id/priceline/cancel endpoint
  * @remarks
  * to cancel flight booking by Admin
  * 
  * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
  */
  adminCancelFlightBooking(id: string, payload: any) {
    return this.http.put<any>(`bookings/${id}/priceline/cancel`, payload)
      .pipe(map((resp: any) => {
        this.cache.remove(`bookings/${id}/priceline/cancel`);
        this.cache.remove(`bookings/${id}/priceline/cancel`);
        return resp;
      }))
  }

   /**
  * Sends a POST request to the bookings/flight/:id/resend-confirmation endpoint
  * @remarks
  * to resend flight booking confirmation
  * 
  * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
  */
  resendFlightBookingConfirmation(id: string) {
    return this.http.put<any>(`bookings/flight/${id}/resend-confirmation`, {})
      .pipe(map((resp: any) => {
        this.cache.remove(`bookings/flight/${id}/resend-confirmation`);
        this.cache.remove(`bookings/flight/${id}/resend-confirmation`);
        return resp;
      }))
  }

 

   /**
  * Sends a GET request to the /flights/seats/${id} endpoint
  * @remarks
  * to get flight seat map
  * 
  * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
  */
  getFlightSeatMap(id: string) {
    return this.http.get<any>(`${this.endpoint}/seats/${id}`)
      .pipe(map((resp: any) => {
        this.cache.remove(`${this.endpoint}/seats/${id}`);
        this.cache.remove(`${this.endpoint}/seats/${id}`);
        return resp;
      }))
  }



}
